import type { BlocksContent } from '@strapi/blocks-react-renderer';

/**
 * Whether a Strapi richtext value would render anything visible.
 *
 * Needed because an "empty" richtext field is rarely `undefined`. Clicking into
 * one and leaving it stores a paragraph holding a single empty text node, which
 * is a truthy array — so `Boolean(field)` reports content that renders as a
 * blank line. Callers that fall back when a field is empty (see the pricing
 * widgets' `fixedPrice`) have to ask this instead.
 *
 * A node counts as content when it carries non-whitespace text, or when it is a
 * node that renders without text at all — an image is still content even though
 * it has none.
 */
export function hasRichTextContent(
  nodes: BlocksContent | undefined | null,
): boolean {
  return Array.isArray(nodes) && nodes.some(nodeHasContent);
}

function nodeHasContent(node: unknown): boolean {
  if (node == null || typeof node !== 'object') {
    return false;
  }
  const obj = node as Record<string, unknown>;

  if (obj.type === 'image') {
    return true;
  }
  if (typeof obj.text === 'string') {
    return obj.text.trim() !== '';
  }
  return Array.isArray(obj.children) && obj.children.some(nodeHasContent);
}
